import { sum } from "lodash";
import { makeAutoObservable } from "mobx";

import {
  CALORIES_PER_CARBOHYDRATE_GRAM,
  CALORIES_PER_FAT_GRAM,
  CALORIES_PER_PROTEIN_GRAM,
  FOOD_EXCHANGE_MULTIPLIERS,
} from "@/config/constants";
import { MealPlanAttributes } from "@/gql/graphql";
import { round } from "@/utils/number";
import { toNum } from "@/utils/string";

import MealPlanFormStore from "./MealPlanFormStore";

export default class FoodExchange {
  use = false;

  // To make working with html inputs easier, we'll mainly update these as
  // strings and manually convert them to numbers during calculations.
  values = {
    vegetable: "",
    fruit: "",
    fat: "",
    sugar: "",
    meatLowFat: "",
    meatModerateFat: "",
    meatHighFat: "",
    milkWhole: "",
    milkLowFat: "",
    milkNonFatSkim: "",
    riceLowProtein: "",
    riceMediumProtein: "",
    riceHighProtein: "",
  };

  private store: MealPlanFormStore;

  get valuesAsEntries(): [keyof FoodExchange["values"], string][] {
    return Object.entries(this.values) as any;
  }

  get calculations() {
    let totalCalories = 0;
    let totalCarbohydrateGrams = 0;
    let totalProteinGrams = 0;
    let totalFatGrams = 0;

    this.valuesAsEntries.forEach(([fel, value]) => {
      totalCalories += +value * FOOD_EXCHANGE_MULTIPLIERS[fel].calories;
      totalCarbohydrateGrams +=
        +value * FOOD_EXCHANGE_MULTIPLIERS[fel].carbohydrates;
      totalProteinGrams += +value * FOOD_EXCHANGE_MULTIPLIERS[fel].proteins;
      totalFatGrams += +value * FOOD_EXCHANGE_MULTIPLIERS[fel].fats;
    });

    const totalCarbohydrateCalories =
      totalCarbohydrateGrams * CALORIES_PER_CARBOHYDRATE_GRAM;
    const totalProteinCalories = totalProteinGrams * CALORIES_PER_PROTEIN_GRAM;
    const totalFatCalories = totalFatGrams * CALORIES_PER_FAT_GRAM;

    const totalCarbohydratePercent = this.getTotalPercentage(
      totalCarbohydrateCalories,
      this.store.macronutrients.carbohydrateCalories,
      this.store.macronutrients.carbohydratePercent
    );
    const totalProteinPercent = this.getTotalPercentage(
      totalProteinCalories,
      this.store.macronutrients.proteinCalories,
      this.store.macronutrients.proteinPercent
    );
    const totalFatPercent = this.getTotalPercentage(
      totalFatCalories,
      this.store.macronutrients.fatCalories,
      this.store.macronutrients.fatPercent
    );

    return {
      totalCalories,
      totalCarbohydrateGrams,
      totalProteinGrams,
      totalFatGrams,
      totalCarbohydrateCalories,
      totalProteinCalories,
      totalFatCalories,
      totalCarbohydratePercent,
      totalProteinPercent,
      totalFatPercent,
    };
  }

  get autosuggest() {
    const { carbohydrateGrams, proteinGrams, fatGrams } =
      this.store.macronutrients;

    const result = {
      riceLowProtein: 0,
      riceMediumProtein: 0,
      riceHighProtein: 0,
      meatLowFat: 0,
      meatModerateFat: 0,
      meatHighFat: 0,
      fat: 0,
    };

    const vegetable = +this.values.vegetable;
    const vegetableValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.vegetable.carbohydrates * vegetable,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.vegetable.proteins * vegetable,
      fats: FOOD_EXCHANGE_MULTIPLIERS.vegetable.fats * vegetable,
    };

    const fruit = +this.values.fruit;
    const fruitValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.fruit.carbohydrates * fruit,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.fruit.proteins * fruit,
      fats: FOOD_EXCHANGE_MULTIPLIERS.fruit.fats * fruit,
    };

    const sugar = +this.values.sugar;
    const sugarValues = {
      carbs: FOOD_EXCHANGE_MULTIPLIERS.sugar.carbohydrates * sugar,
      proteins: FOOD_EXCHANGE_MULTIPLIERS.sugar.proteins * sugar,
      fats: FOOD_EXCHANGE_MULTIPLIERS.sugar.fats * sugar,
    };

    const milkNonFatSkim = +this.values.milkNonFatSkim;
    const milkLowFat = +this.values.milkLowFat;
    const milkWhole = +this.values.milkWhole;
    const MILK_MULIPLIERS = FOOD_EXCHANGE_MULTIPLIERS.milk;

    const milkValues = {
      nonFatSkim: {
        carbs: MILK_MULIPLIERS.nonFatSkim.carbohydrates * milkNonFatSkim,
        proteins: MILK_MULIPLIERS.nonFatSkim.proteins * milkNonFatSkim,
        fats: MILK_MULIPLIERS.nonFatSkim.fats * milkNonFatSkim,
      },
      lowFat: {
        carbs: MILK_MULIPLIERS.lowFat.carbohydrates * milkLowFat,
        proteins: MILK_MULIPLIERS.lowFat.proteins * milkLowFat,
        fats: MILK_MULIPLIERS.lowFat.fats * milkLowFat,
      },
      whole: {
        carbs: MILK_MULIPLIERS.whole.carbohydrates * milkWhole,
        proteins: MILK_MULIPLIERS.whole.proteins * milkWhole,
        fats: MILK_MULIPLIERS.whole.fats * milkWhole,
      },
    };

    const riceLowProtein = +this.values.riceLowProtein;
    const riceMediumProtein = +this.values.riceMediumProtein;
    const riceHighProtein = +this.values.riceHighProtein;
    const RICE_MULTIPLIERS = FOOD_EXCHANGE_MULTIPLIERS.rice;
    const riceValues = {
      lowProtein: {
        carbs: RICE_MULTIPLIERS.lowProtein.carbohydrates * riceLowProtein,
        proteins: RICE_MULTIPLIERS.lowProtein.proteins * riceLowProtein,
        fats: RICE_MULTIPLIERS.lowProtein.fats * riceLowProtein,
      },
      mediumProtein: {
        carbs: RICE_MULTIPLIERS.mediumProtein.carbohydrates * riceMediumProtein,
        proteins: RICE_MULTIPLIERS.mediumProtein.proteins * riceMediumProtein,
        fats: RICE_MULTIPLIERS.mediumProtein.fats * riceMediumProtein,
      },
      highProtein: {
        carbs: RICE_MULTIPLIERS.highProtein.carbohydrates * riceHighProtein,
        proteins: RICE_MULTIPLIERS.highProtein.proteins * riceHighProtein,
        fats: RICE_MULTIPLIERS.highProtein.fats * riceHighProtein,
      },
    };

    const totalRice = sum([riceLowProtein, riceMediumProtein, riceHighProtein]);

    const totalMeat = sum([
      +this.values.meatLowFat,
      +this.values.meatModerateFat,
      +this.values.meatHighFat,
    ]);

    const totalFat = +this.values.fat;

    /**
     * Calculate starch first.
     *
     * Renamed to rice
     *
     * After adding vegetable, fruit, milk and sugar,
     * get the sum of Carbohydrates
     */

    const milkCarbohydrates = sum([
      milkValues.nonFatSkim.carbs,
      milkValues.lowFat.carbs,
      milkValues.whole.carbs,
    ]);

    const totalCarbohydrates = sum([
      vegetableValues.carbs,
      fruitValues.carbs,
      sugarValues.carbs,
      milkCarbohydrates,
    ]);

    const _suggestedRiceLowProtein = this.suggestFoodExchange(
      carbohydrateGrams,
      totalCarbohydrates,
      RICE_MULTIPLIERS.lowProtein.carbohydrates
    );
    const suggestedRiceLowProtein = Math.max(
      0,
      _suggestedRiceLowProtein - totalRice
    );

    const _suggestedRiceMediemProtein = this.suggestFoodExchange(
      carbohydrateGrams,
      totalCarbohydrates,
      RICE_MULTIPLIERS.mediumProtein.carbohydrates
    );
    const suggestedRiceMediumProtein = Math.max(
      0,
      _suggestedRiceMediemProtein - totalRice
    );

    const _suggestedRiceHighProtein = this.suggestFoodExchange(
      carbohydrateGrams,
      totalCarbohydrates,
      RICE_MULTIPLIERS.highProtein.carbohydrates
    );
    const suggestedRiceHighProtein = Math.max(
      0,
      _suggestedRiceHighProtein - totalRice
    );

    result.riceLowProtein = round(suggestedRiceLowProtein, 1);
    result.riceMediumProtein = round(suggestedRiceMediumProtein, 1);
    result.riceHighProtein = round(suggestedRiceHighProtein, 1);

    /**
     * Calculate meat
     *
     * After finalizing the number of exchanges for the Starch/Grains,
     * this time get the sum of total Protein content.
     * From Vegetable to Starch/Grains Food Groups
     */

    const riceProteins = sum([
      riceValues.lowProtein.proteins,
      riceValues.mediumProtein.proteins,
      riceValues.highProtein.proteins,
    ]);
    const milkProteins = sum([
      milkValues.nonFatSkim.proteins,
      milkValues.lowFat.proteins,
      milkValues.whole.proteins,
    ]);
    const totalProteins = sum([
      vegetableValues.proteins,
      fruitValues.proteins,
      sugarValues.proteins,
      milkProteins,
      riceProteins,
    ]);

    const _suggestedLowFatMeat = this.suggestFoodExchange(
      proteinGrams,
      totalProteins,
      FOOD_EXCHANGE_MULTIPLIERS.meat.lowFat.proteins
    );
    const suggestedLowFatMeat = Math.max(0, _suggestedLowFatMeat - totalMeat);
    const _suggestedModerateFatMeat = this.suggestFoodExchange(
      proteinGrams,
      totalProteins,
      FOOD_EXCHANGE_MULTIPLIERS.meat.moderateFat.proteins
    );
    const suggestedModerateFatMeat = Math.max(
      0,
      _suggestedModerateFatMeat - totalMeat
    );
    const _suggestedHighFatMeat = this.suggestFoodExchange(
      proteinGrams,
      totalProteins,
      FOOD_EXCHANGE_MULTIPLIERS.meat.highFat.proteins
    );
    const suggestedHighFatMeat = Math.max(0, _suggestedHighFatMeat - totalMeat);

    result.meatLowFat = round(suggestedLowFatMeat, 1);
    result.meatModerateFat = round(suggestedModerateFatMeat, 1);
    result.meatHighFat = round(suggestedHighFatMeat, 1);

    /**
     *  Calculate fat
     *
     *  Then same procedure, after finalizing the number of exchanges for the Meat,
     *  this time get the sum of total Fat content.
     *  From Dairy/Milk to Meat Food Groups
     */

    const riceFats = sum([
      riceValues.lowProtein.fats,
      riceValues.mediumProtein.fats,
      riceValues.highProtein.fats,
    ]);
    const milkFats = sum([
      milkValues.nonFatSkim.fats,
      milkValues.lowFat.fats,
      milkValues.whole.fats,
    ]);
    const meatFats = sum([
      suggestedLowFatMeat,
      suggestedModerateFatMeat,
      suggestedHighFatMeat,
    ]);
    const totalFats = sum([
      vegetableValues.fats,
      fruitValues.fats,
      sugarValues.fats,
      riceFats,
      milkFats,
      meatFats,
    ]);

    const _suggestedFat = this.suggestFoodExchange(
      fatGrams,
      totalFats,
      FOOD_EXCHANGE_MULTIPLIERS.fat.fats
    );

    const suggestedFat = Math.max(0, _suggestedFat - totalFat);

    result.fat = round(suggestedFat, 1);

    return result;
  }

  constructor(store: MealPlanFormStore) {
    makeAutoObservable(this);
    this.store = store;
  }

  toggle(use: boolean): void {
    this.use = use;
  }

  set(name: keyof FoodExchange["values"], value: string): void {
    this.values[name] = value;
  }

  toAttributes(): Partial<MealPlanAttributes> {
    if (!this.use) return {};
    return {
      foodExchangeFat: toNum(this.values.fat),
      foodExchangeFruit: toNum(this.values.fruit),
      foodExchangeMeatHighFat: toNum(this.values.meatHighFat),
      foodExchangeMeatLowFat: toNum(this.values.meatLowFat),
      foodExchangeMeatModerateFat: toNum(this.values.meatModerateFat),
      foodExchangeMilkLowFat: toNum(this.values.milkLowFat),
      foodExchangeMilkNonFatSkim: toNum(this.values.milkNonFatSkim),
      foodExchangeMilkWhole: toNum(this.values.milkWhole),
      foodExchangeRiceHighProtein: toNum(this.values.riceHighProtein),
      foodExchangeRiceLowProtein: toNum(this.values.riceLowProtein),
      foodExchangeRiceMediumProtein: toNum(this.values.riceMediumProtein),
      foodExchangeSugar: toNum(this.values.sugar),
      foodExchangeVegetable: toNum(this.values.vegetable),
    };
  }

  private getTotalPercentage(
    calories: number,
    totalCalories: number,
    maxPercentage: number
  ) {
    if (maxPercentage === 0) return 0;
    // `|| 1` is there to prevent division by zero.
    const _result = calories / (totalCalories || 1);
    return _result * maxPercentage;
  }

  private suggestFoodExchange(
    macroInGrams: number,
    totalFelMacroGrams: number,
    gramsPerExchange: number
  ) {
    const result = (macroInGrams - totalFelMacroGrams) / gramsPerExchange;

    if (isNaN(result) || result < 0) return 0;

    return result;
  }
}
